const CURSOR_SIZE_PX = 28;
const HERO_SELECTOR = '.scroll-hero-section';
const HOVER_SELECTOR =
  'a, button, [role="button"], .hero-cta-primary, .hero-cta-secondary';

/**
 * Custom cursor (pointer.png / cursor.png) only in the hero section.
 * Default system cursor everywhere else.
 */
export class CustomCursor {
  private heroSection: HTMLElement | null = null;
  private container: HTMLElement | null = null;
  private imgPointer: HTMLImageElement | null = null;
  private imgCursor: HTMLImageElement | null = null;
  private x = 0;
  private y = 0;
  private rafId: number | null = null;
  private visible = false;
  private isHover = false;
  private boundMove: (e: MouseEvent) => void;
  private boundEnter: () => void;
  private boundLeave: (e: MouseEvent) => void;

  constructor() {
    this.boundMove = this.onMouseMove.bind(this);
    this.boundEnter = this.onEnter.bind(this);
    this.boundLeave = this.onLeave.bind(this);
  }

  init(): void {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.container = document.createElement('div');
    this.container.id = 'custom-cursor';
    this.container.setAttribute('aria-hidden', 'true');
    this.container.className = 'custom-cursor';

    this.imgPointer = document.createElement('img');
    this.imgPointer.src = '/pointer.png';
    this.imgPointer.alt = '';
    this.imgPointer.className = 'custom-cursor__img custom-cursor__img--pointer';
    this.imgPointer.width = CURSOR_SIZE_PX;
    this.imgPointer.height = CURSOR_SIZE_PX;

    this.imgCursor = document.createElement('img');
    this.imgCursor.src = '/cursor.png';
    this.imgCursor.alt = '';
    this.imgCursor.className = 'custom-cursor__img custom-cursor__img--cursor';
    this.imgCursor.width = CURSOR_SIZE_PX;
    this.imgCursor.height = CURSOR_SIZE_PX;

    this.container.appendChild(this.imgPointer);
    this.container.appendChild(this.imgCursor);

    this.heroSection = document.querySelector(HERO_SELECTOR);
    document.body.appendChild(this.container);

    document.addEventListener('mousemove', this.boundMove, { passive: true });
    document.addEventListener('mouseenter', this.boundEnter, true);
    document.addEventListener('mouseleave', this.boundLeave, true);

    this.tick();
  }

  private onMouseMove(e: MouseEvent): void {
    this.x = e.clientX;
    this.y = e.clientY;
    this.visible = true;
  }

  private onEnter(): void {
    this.visible = true;
  }

  private onLeave(e: MouseEvent): void {
    if (e.relatedTarget === null || !document.body.contains(e.relatedTarget as Node)) {
      this.visible = false;
    }
  }

  /** True only when cursor is over the visible part of the hero (hero rect ∩ viewport). */
  private isOverVisibleHero(): boolean {
    if (!this.heroSection) return false;
    const rect = this.heroSection.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const left = Math.max(rect.left, 0);
    const right = Math.min(rect.right, vw);
    const top = Math.max(rect.top, 0);
    const bottom = Math.min(rect.bottom, vh);
    if (left >= right || top >= bottom) return false;
    return this.x >= left && this.x <= right && this.y >= top && this.y <= bottom;
  }

  private tick = (): void => {
    this.rafId = requestAnimationFrame(this.tick);
    if (!this.container || !this.imgPointer || !this.imgCursor) return;

    this.container.style.transform = `translate(${this.x}px, ${this.y}px) translate(-50%, -50%)`;

    const inHero = this.isOverVisibleHero();
    const showCustom = this.visible && inHero;

    if (showCustom) {
      document.body.classList.add('custom-cursor-active');
      this.container.style.opacity = '1';

      const elements = document.elementsFromPoint(this.x, this.y);
      const under = elements.find((el) => !this.container?.contains(el));
      this.isHover = !!(under && (under as Element).closest(HOVER_SELECTOR));
      const showPointer = !this.isHover;
      this.imgPointer.style.opacity = showPointer ? '1' : '0';
      this.imgCursor.style.opacity = this.isHover ? '1' : '0';
    } else {
      document.body.classList.remove('custom-cursor-active');
      this.container.style.opacity = '0';
    }
    this.imgPointer.style.pointerEvents = 'none';
    this.imgCursor.style.pointerEvents = 'none';
  };

  dispose(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    document.removeEventListener('mousemove', this.boundMove);
    document.removeEventListener('mouseenter', this.boundEnter, true);
    document.removeEventListener('mouseleave', this.boundLeave, true);
    document.body.classList.remove('custom-cursor-active');
    this.container?.remove();
    this.heroSection = null;
    this.container = null;
    this.imgPointer = null;
    this.imgCursor = null;
  }
}
